import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { testIds } from './testIds';
import { EmployerDocUploadField } from '../components/EmployerDocUploadField';
import type { PlacementDetailsState, PlacementContract } from './types';

function ContractField({
  label,
  value,
  'data-testid': dataTestId,
}: {
  label: React.ReactNode;
  value: string | null;
  'data-testid': string;
}): React.ReactElement {
  return (
    <Box data-testid={dataTestId}>
      <Text fontSize="sm" color="fg.muted">
        {label}
      </Text>
      <Text fontWeight="medium">{value ?? '—'}</Text>
    </Box>
  );
}

function ContractParties({ contract }: { contract: PlacementContract }): React.ReactElement {
  const { employer, employerContact, documentUrl } = contract;
  return (
    <VStack align="stretch" gap={2}>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        <ContractField
          label={<Trans>Employer</Trans>}
          value={employer.name}
          data-testid={testIds.contractEmployer}
        />
        <ContractField
          label={<Trans>Contact</Trans>}
          value={
            employerContact != null
              ? `${employerContact.firstName} ${employerContact.lastName}`
              : null
          }
          data-testid={testIds.contractContact}
        />
      </SimpleGrid>
      <Text fontSize="sm" color="fg.muted">
        <Trans>Set by the student's placement contract.</Trans>{' '}
        {documentUrl != null ? (
          <Link
            href={documentUrl}
            isExternal
            color="fg.accent.emphasis"
            fontWeight="medium"
            data-testid={testIds.contractDocumentLink}
          >
            <Trans>View contract</Trans>
          </Link>
        ) : null}
      </Text>
    </VStack>
  );
}

export function PlacementDetails({
  contract,
  value,
  onChange,
}: {
  contract: PlacementContract;
  value: PlacementDetailsState;
  onChange: (next: PlacementDetailsState) => void;
}): React.ReactElement {
  return (
    <VStack align="stretch" gap={4} w="full">
      <ContractParties contract={contract} />

      <FormControl isRequired>
        <FormLabel>
          <Trans>Start date</Trans>
        </FormLabel>
        <Input
          type="date"
          data-testid={testIds.startDate}
          value={value.startedAt}
          onChange={(event) => onChange({ ...value, startedAt: event.target.value })}
        />
      </FormControl>

      <EmployerDocUploadField
        label={<Trans>Workplace agreement</Trans>}
        kind="WORK_PLACEMENT_AGREEMENT"
        isRequired={false}
        doc={value.agreement}
        onUploaded={(doc) => onChange({ ...value, agreement: doc })}
        onClear={() => onChange({ ...value, agreement: null })}
        data-testid={testIds.agreementField}
      />
    </VStack>
  );
}
